import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormTextField } from '@skupreme/shared/ui/form';
import { ErrorMessageConstant } from '@utility/constants/common';
import OAuthService from '@utility/services/oauth';
import {
  ConnectIntro,
  ConnectPopup,
  failureText,
  type ConnectMarketplaceItem,
} from './ConnectPopup';

const formSchema = z.object({
  seller_id: z.string().min(1, 'Seller Id is required!'),
  authorization_key: z.string().min(1, 'Authorization key is required!'),
  email: z.string().min(1, 'Email is required').email('Must be a valid Email'),
});

type SearsValues = z.infer<typeof formSchema>;

const EMPTY: SearsValues = { seller_id: '', authorization_key: '', email: '' };

type SearsConnectPopupProps = {
  item: ConnectMarketplaceItem | null;
  open: boolean;
  closeModal: (success?: boolean) => void;
};

export function SearsConnectPopup({
  item,
  open,
  closeModal,
}: SearsConnectPopupProps) {
  const { control, handleSubmit, reset, clearErrors } = useForm<SearsValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY,
    shouldUnregister: true,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const close = (connected?: boolean) => {
    reset(EMPTY);
    clearErrors();
    setSuccess('');
    setError('');
    closeModal(connected);
  };

  const onSubmit = async (data: SearsValues) => {
    try {
      setSaving(true);
      setError('');

      await OAuthService.searsIntegrate({
        marketplace_id: item?.marketplace_id,
        marketplace_region_id: item?.marketplace_region_id,
        seller_id: data.seller_id,
        authorization_key: data.authorization_key,
        email: data.email,
      });
      setSuccess('Sears integrated successfully');

      setTimeout(() => {
        close(true);
      }, 2000);
    } catch (failure) {
      setError(
        failureText(failure, 'error', ErrorMessageConstant.defaultAPIError),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConnectPopup
      open={open}
      title={`${item?.alias ?? ''} Integration`}
      onClose={() => close()}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Submit"
      isSubmitting={saving}
      error={error}
      success={success}
    >
      <ConnectIntro logo="/images/marketplaces/sears.svg">
        Please paste your {item?.alias} API credentials here
      </ConnectIntro>
      <FormTextField
        control={control}
        name="seller_id"
        label="Seller Id"
        isRequired
      />
      <FormTextField
        control={control}
        name="authorization_key"
        label="Authorization Key"
        isRequired
      />
      <FormTextField control={control} name="email" label="Email" isRequired />
    </ConnectPopup>
  );
}
